/**
 * React Query hooks for Calendar Event Files
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { calendarEventFilesApi } from '@api/endpoints/calendarEventFiles'
import {
  GetCalendarEventFilesParams,
  UpdateCalendarEventMediaFileRequest,
  CalendarEventFileUploadOptions,
} from '@api/types/calendarEventFiles'
import { calendarEventKeys } from './useCalendarEvents'

// ====================
// QUERY KEYS
// ====================

export const calendarEventFileKeys = {
  all: ['calendarEventFiles'] as const,
  lists: () => [...calendarEventFileKeys.all, 'list'] as const,
  list: (eventId: string, params?: GetCalendarEventFilesParams) =>
    [...calendarEventFileKeys.lists(), eventId, params] as const,
  details: () => [...calendarEventFileKeys.all, 'detail'] as const,
  detail: (eventId: string, fileId: string) =>
    [...calendarEventFileKeys.details(), eventId, fileId] as const,
  quota: (eventId: string) => [...calendarEventFileKeys.all, 'quota', eventId] as const,
}

// ====================
// QUERIES
// ====================

/**
 * Get files for an event
 */
export function useCalendarEventFiles(eventId: string, params?: GetCalendarEventFilesParams) {
  return useQuery({
    queryKey: calendarEventFileKeys.list(eventId, params),
    queryFn: () => calendarEventFilesApi.getFiles(eventId, params),
    enabled: !!eventId,
  })
}

/**
 * Get single file details
 */
export function useCalendarEventFile(eventId: string, fileId: string) {
  return useQuery({
    queryKey: calendarEventFileKeys.detail(eventId, fileId),
    queryFn: () => calendarEventFilesApi.getFile(eventId, fileId),
    enabled: !!eventId && !!fileId,
  })
}

/**
 * Get storage quota for an event
 */
export function useCalendarEventStorageQuota(eventId: string) {
  return useQuery({
    queryKey: calendarEventFileKeys.quota(eventId),
    queryFn: () => calendarEventFilesApi.getStorageQuota(eventId),
    enabled: !!eventId,
  })
}

// ====================
// MUTATIONS
// ====================

/**
 * Upload file to event
 */
export function useUploadCalendarEventFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      eventId,
      file,
      options,
    }: {
      eventId: string
      file: File
      options?: CalendarEventFileUploadOptions
    }) => calendarEventFilesApi.uploadFile(eventId, file, options),
    onSuccess: (_, variables) => {
      // Invalidate file list and quota
      queryClient.invalidateQueries({
        queryKey: calendarEventFileKeys.list(variables.eventId),
      })
      queryClient.invalidateQueries({
        queryKey: calendarEventFileKeys.quota(variables.eventId),
      })
    },
  })
}

/**
 * Update file metadata
 */
export function useUpdateCalendarEventFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      eventId,
      fileId,
      data,
    }: {
      eventId: string
      fileId: string
      data: UpdateCalendarEventMediaFileRequest
    }) => calendarEventFilesApi.updateFile(eventId, fileId, data),
    onSuccess: (result, variables) => {
      // Update detail cache
      queryClient.setQueryData(
        calendarEventFileKeys.detail(variables.eventId, variables.fileId),
        result
      )
      // Invalidate list
      queryClient.invalidateQueries({
        queryKey: calendarEventFileKeys.list(variables.eventId),
      })
    },
  })
}

/**
 * Delete file from event
 */
export function useDeleteCalendarEventFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ eventId, fileId }: { eventId: string; fileId: string }) =>
      calendarEventFilesApi.deleteFile(eventId, fileId),
    onSuccess: (_, variables) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: calendarEventFileKeys.detail(variables.eventId, variables.fileId),
      })
      // Invalidate list and quota
      queryClient.invalidateQueries({
        queryKey: calendarEventFileKeys.list(variables.eventId),
      })
      queryClient.invalidateQueries({
        queryKey: calendarEventFileKeys.quota(variables.eventId),
      })
    },
  })
}

/**
 * Set cover image for event
 */
export function useSetCalendarEventCoverImage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ eventId, mediaFileId }: { eventId: string; mediaFileId: string | null }) =>
      calendarEventFilesApi.setCoverImage(eventId, { mediaFileId }),
    onSuccess: (_, variables) => {
      // Invalidate event detail to refresh cover image
      queryClient.invalidateQueries({
        queryKey: calendarEventKeys.detail(variables.eventId),
      })
      queryClient.invalidateQueries({
        queryKey: calendarEventKeys.edit(variables.eventId),
      })
      // Invalidate file list
      queryClient.invalidateQueries({
        queryKey: calendarEventFileKeys.list(variables.eventId),
      })
    },
  })
}
